// RAL-142: posthog-js used to be a static import in main.tsx with `.init()` called
// synchronously at module scope — Lighthouse's critical-request-chain trace showed
// `index.js -> posthog /e/ event beacon` as the single longest chain on the page (495ms).
// Every caller now goes through this module instead of importing posthog-js directly, so
// the library (and its init network beacon) is only ever pulled in lazily, off the initial
// render path, no matter which component fires the first event.
//
// Session recording and surveys are explicitly disabled below: a codebase grep (RAL-142)
// found no caller of `posthog.startSessionRecording` or any survey API, so neither
// sub-bundle (posthog-recorder.js, surveys.js — 28.7 KiB of legacy-JS polyfills per the
// ticket) is ever fetched. Revisit this if either feature becomes an active requirement —
// that's a product/analytics-owner call, not one this fix makes silently.

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST

export const posthogConfigured = Boolean(POSTHOG_KEY && POSTHOG_HOST)

// This validation used to live only in main.tsx, which no test file ever imported, so it
// never ran under CI (where VITE_POSTHOG_KEY/HOST are deliberately unset). Moving it here
// means the many component tests that transitively import this module now execute it too
// — `import.meta.env.VITEST` (set by Vitest itself) keeps the throw scoped to an actual dev
// server run, not a test run missing the same config on purpose.
const inTestRun = Boolean(import.meta.env.VITEST)

/**
 * Which PostHog param is missing in dev, or null when the config is fine.
 *
 * This used to `throw` at module scope. The intent was right — silently missing analytics is worse
 * than a loud failure — but a throw during ESM evaluation kills the whole module graph, so
 * `createRoot(...).render(...)` never ran and the app rendered a blank page with no console error
 * and no Vite overlay. React itself loaded, which made it look like the app had mounted and
 * rendered nothing. It was the quietest possible failure, not the loudest (RAL-204).
 *
 * `.env` could not rescue it either: vite.config.ts defines these from `params()`
 * unconditionally, so the only way to supply them is a PARAMS blob. Following the documented
 * local-dev recipe, which sets only CLERK_PUBLISHABLE_KEY, produced exactly this blank page.
 *
 * The check still runs and is still impossible to ignore — it logs and renders a banner — but it
 * no longer takes the app down with it. Production is untouched: this is DEV-only, as before.
 */
export const posthogConfigError: string | null =
  import.meta.env.DEV && !inTestRun
    ? !POSTHOG_KEY
      ? 'VITE_POSTHOG_KEY is missing or un-configured, so analytics events are being silently dropped.'
      : !POSTHOG_HOST
        ? 'VITE_POSTHOG_HOST is missing or un-configured, so analytics events are being silently dropped.'
        : null
    : null

if (posthogConfigError) {
  // Logged as well as rendered: whichever the developer is looking at, they see it.
  console.error(
    `[PostHog] ${posthogConfigError} Set it in the PARAMS blob you start Vite with — see CLAUDE.md's local-dev recipe. This message stops once it is configured.`
  )
}

type PostHog = typeof import('posthog-js').default

// requestIdleCallback pushes the load past first paint where supported; Safari has no
// requestIdleCallback so this falls back to a macrotask via setTimeout.
const schedule = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 0)

let posthogPromise: Promise<PostHog> | null = null

// The one load path every caller (capture/identify/reset/initPostHog) goes through, so the
// idle deferral applies consistently no matter which fires first — an already-signed-in
// user's identify call on mount is just as deferred as the explicit initPostHog() call. On
// a rejected import (e.g. a stale chunk hash after a deploy), resets posthogPromise so the
// next call retries instead of every future call reusing the same dead rejected promise.
const loadPosthog = (): Promise<PostHog> => {
  posthogPromise ??= new Promise<PostHog>((resolve, reject) => {
    schedule(() => {
      import('posthog-js')
        .then(({ default: posthog }) => {
          posthog.init(POSTHOG_KEY as string, {
            api_host: POSTHOG_HOST as string,
            defaults: '2026-01-30',
            capture_exceptions: {
              capture_unhandled_errors: true,
              capture_unhandled_rejections: true,
              capture_console_errors: false
            },
            disable_session_recording: true,
            disable_surveys: true
          })
          resolve(posthog)
        })
        .catch(reject)
    })
  }).catch((err: unknown) => {
    posthogPromise = null
    throw err
  })
  return posthogPromise
}

// Kicks off the deferred load/init from AuthSync's mount effect. Fire-and-forget: nothing
// downstream is waiting on this, it just warms loadPosthog()'s cache ahead of the first
// real event.
export const initPostHog = (): void => {
  if (!posthogConfigured) return
  loadPosthog().catch(() => {})
}

export const posthogCapture = (event: string, properties?: Record<string, unknown>): void => {
  if (!posthogConfigured) return
  loadPosthog()
    .then((posthog) => posthog.capture(event, properties))
    .catch(() => {})
}

export const posthogIdentify = (userId: string): void => {
  if (!posthogConfigured) return
  loadPosthog()
    .then((posthog) => posthog.identify(userId))
    .catch(() => {})
}

export const posthogReset = (): void => {
  if (!posthogConfigured) return
  loadPosthog()
    .then((posthog) => posthog.reset())
    .catch(() => {})
}
